import { useState, useEffect } from 'react';
import { useApp } from '../App';
import api from '../api';
import BackButton from '../components/BackButton';
import { useI18n } from '../i18n/index.jsx';

const API_BASE = import.meta.env.VITE_API_URL || 'https://api.workifypl.com';

const VAT_OPTIONS = [
  { value: '23', label: '23%' },
  { value: '8', label: '8%' },
  { value: '5', label: '5%' },
  { value: '0', label: '0%' },
  { value: 'zw', label: 'zw (exempt)' },
  { value: 'np', label: 'np (not applicable)' },
  { value: 'rc', label: 'reverse charge (EU B2B)' },
];

export default function Proforma() {
  const { user, currentCompany } = useApp();
  const { t } = useI18n();
  const [bankAccounts, setBankAccounts] = useState([]);
  const [currency, setCurrency] = useState('PLN');
  const [buyerType, setBuyerType] = useState('company');
  const [nipLoading, setNipLoading] = useState(false);
  const [msg, setMsg] = useState('');
  const [proformas, setProformas] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editId, setEditId] = useState(null);
  const [editCompanyId, setEditCompanyId] = useState(null);
  const [form, setForm] = useState({
    client_name: '',
    client_nip: '',
    client_address: '',
    service_name: '',
    quantity: 1,
    unit_price: '',
    vat_rate: '23',
    payment_method: 'przelew',
    payment_days: 14,
    notes: '',
  });

  useEffect(() => {
    loadProformas();
  }, []);

  useEffect(() => {
    if (currentCompany?.id) loadBankAccounts(currentCompany.id);
  }, [currentCompany?.id]);

  async function loadProformas() {
    try {
      const params = currentCompany?.id ? `?company_id=${currentCompany.id}` : ''; const { data } = await api.get(`/invoices/out${params}`);
      setProformas(data || []);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  }

  async function loadBankAccounts(companyId) {
    try {
      const { data } = await api.get(`/companies/${companyId}/bank-accounts`);
      const accs = data || [];
      setBankAccounts(accs);
      if (accs.length > 0) {
        const def = accs.find(a => a.is_default) || accs[0];
        setCurrency(def.currency);
      }
    } catch (e) {
      console.error(e);
    }
  }

  async function lookupNip(nip) {
    const raw = (nip || '').trim();
    const upper = raw.toUpperCase();
    const isEU = /^[A-Z]{2}[A-Z0-9]+$/.test(upper);
    const cleanNip = raw.replace(/[^0-9]/g, '');
    if (!isEU && cleanNip.length !== 10) {
      setMsg('❌ Invalid NIP/VAT');
      return;
    }
    setNipLoading(true);
    try {
      if (isEU) {
        const res = await api.get(`/vat/lookup?vat=${upper}`);
        const data = res.data;

        if (data.valid) {
          setForm(f => ({
            ...f,
            client_name: data.name || f.client_name,
            client_address: data.address || f.client_address,
            client_nip: upper,
          }));
          setMsg('✅ Dane firmy pobrane z VIES');
          return;
        }
      }
      const res = await api.get(`/krs/nip/${cleanNip}`);
      if (res.data && res.data.name) {
        setForm(f => ({
          ...f,
          client_name: res.data.name,
          client_address: res.data.address || f.client_address,
          client_nip: cleanNip,
        }));
        setMsg('✅ Dane firmy pobrane z bazy');
      }
    } catch (e) {
      try {
        const gusRes = await fetch(`https://wl-api.mf.gov.pl/api/search/nip/${cleanNip}?date=${new Date().toISOString().split('T')[0]}`);
        const gusData = await gusRes.json();
        if (gusData.result?.subject) {
          const s = gusData.result.subject;
          setForm(f => ({
            ...f,
            client_name: s.name,
            client_address: `${s.workingAddress || s.residenceAddress}`,
            client_nip: cleanNip,
          }));
          setMsg('✅ Dane firmy pobrane z GUS');
        }
      } catch {}
    } finally {
      setNipLoading(false);
    }
  }



  function editProforma(p) {
    setMsg('');
    setEditId(p.id);
    setEditCompanyId(p.company_id || null);
    setShowForm(true);
    setForm({
      client_name: p.buyer_name || '',
      client_nip: p.buyer_nip || '',
      client_address: p.buyer_address || '',
      service_name: p.items?.[0]?.description || '',
      quantity: Number(p.items?.[0]?.quantity || 1),
      unit_price: p.items?.[0]?.unit_price || '',
      vat_rate: String(p.items?.[0]?.vat_rate ?? '23'),
      payment_method: p.payment_method || 'przelew',
      payment_days: 14,
      notes: p.notes || '',
    });
    setCurrency(p.currency || 'PLN');
    setBuyerType(p.buyer_nip ? 'company' : 'person');
  }

  async function deleteProforma(p) {
    try {
      await api.delete(`/proforma/out/${p.id}`);
      setMsg('✅ Proforma удалена');
      await loadProformas();
    } catch (e) {
      setMsg('❌ Ошибка удаления'); console.error(e);
    }
  }
  async function sharePdf(p) {
    try {
      const response = await api.get(`/invoices/out/${p.id}/pdf`, { responseType: 'blob' });
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const filename = `Proforma_${(p.invoice_number || 'proforma').replace(/\//g, '_')}.pdf`;
      let shared = false;
      try {
        const file = new File([blob], filename, { type: 'application/pdf' });
        if (navigator.share && navigator.canShare && navigator.canShare({ files: [file] })) {
          await navigator.share({ files: [file], title: filename });
          shared = true;
        }
      } catch (e) {
        // ignore share errors and fallback
      }
      if (!shared) {
        const token = sessionStorage.getItem('jwt');
        const url = `${API_BASE}/invoices/out/${p.id}/pdf-share?token=${encodeURIComponent(token || '')}`;
        window.open(url, '_blank');
      }
    } catch (e) {
      setMsg('❌ Ошибка скачивания PDF: ' + (e.response?.data?.detail || e.message));
      const token = sessionStorage.getItem('jwt');
      if (token) {
        window.open(`${API_BASE}/invoices/out/${p.id}/pdf-share?token=${encodeURIComponent(token)}`, '_blank');
      }
    }
  }
  async function createProforma() {
    if (!form.client_name || !form.service_name || !form.unit_price) {
      alert('Заполните обязательные поля');
      return;
    }
    if (user?.trial_enabled && !user?.has_paid_access && (user.trial_proforma_created >= (user.trial_proforma_limit || 1))) {
      alert('Лимит trial: доступна только 1 proforma. Для продолжения оплатите 1200 PLN.');
      return;
    }

    const vatRateNum = ['zw','np','rc'].includes(form.vat_rate) ? 0 : (parseFloat(form.vat_rate) || 0);
    const netAmount = form.quantity * parseFloat(form.unit_price);
    const vatAmount = netAmount * (vatRateNum / 100);
    const grossAmount = netAmount + vatAmount;

    const rcNote = form.vat_rate === 'rc' ? 'Reverse charge (EU B2B)' : '';
    const notesFinal = rcNote ? [form.notes, rcNote].filter(Boolean).join(' | ') : (form.notes || null);
    setMsg('');
    try {
      if (editId) {
        await api.put(`/proforma/out/${editId}`, {
        company_id: editCompanyId || currentCompany?.id,
        buyer_name: form.client_name,
        buyer_nip: buyerType === 'company' ? (form.client_nip || null) : null,
        buyer_address: form.client_address || null,
        issue_date: new Date().toISOString().split('T')[0],
        due_date: new Date(Date.now() + form.payment_days * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
        currency: currency || 'PLN',
        payment_method: form.payment_method || 'przelew',
        notes: notesFinal,
        items: [{
          description: form.service_name,
          quantity: form.quantity,
          unit: 'szt.',
          unit_price: parseFloat(form.unit_price),
          vat_rate: vatRateNum,
        }],
      });
        setMsg('✅ Proforma обновлена');
      } else {
        await api.post('/proforma/out', {
          company_id: editCompanyId || currentCompany?.id || 1,
          buyer_name: form.client_name,
          buyer_nip: buyerType === 'company' ? (form.client_nip || null) : null,
          buyer_address: form.client_address || null,
          issue_date: new Date().toISOString().split('T')[0],
          due_date: new Date(Date.now() + form.payment_days * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
          currency: currency || 'PLN',
          payment_method: form.payment_method || 'przelew',
          notes: notesFinal,
          items: [{
            description: form.service_name,
            quantity: form.quantity,
            unit: 'szt.',
            unit_price: parseFloat(form.unit_price),
            vat_rate: vatRateNum,
          }],
        });
        setMsg('✅ Proforma создана');
      }
      await loadProformas();
      setShowForm(false);
      setEditId(null);
      setEditCompanyId(null);
      setForm({
        client_name: '',
        client_nip: '',
        client_address: '',
        service_name: '',
        quantity: 1,
        unit_price: '',
        vat_rate: '23',
        payment_method: 'przelew',
        payment_days: 14,
        notes: '',
      });
    } catch (e) {
      setMsg('❌ ' + (e.userMessage || e.response?.data?.detail || 'Ошибка'));
    }
  }


  const statusColors = {
    proforma: '#f59e0b',
    proforma_paid: '#22c55e',
    proforma_converted: '#10b981',
    issued: '#3b82f6',
    paid: '#22c55e',
    verified: '#06b6d4',
    cancelled: '#ef4444',
  };

  const statusLabels = {
    proforma: 'Proforma',
    proforma_paid: 'Оплачена',
    proforma_converted: 'Конвертирована',
    issued: 'Фактура',
    paid: 'Оплачена',
    verified: 'Проверена',
    cancelled: 'Отменена',
  };

  if (loading) return <div className="page"><BackButton /><div className="loader">{t('common.loading')}</div></div>;

  return (
    <div className="page">
      <BackButton />
      <h1>📋 Proforma</h1>
      {msg && <div className="message">{msg}</div>}

      {/* Info */}
      <div style={{
        background: 'linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)',
        borderRadius: 12,
        padding: 16,
        marginBottom: 16,
      }}>
        <div style={{ fontWeight: 'bold', marginBottom: 8 }}>📋 Proforma</div>
        <p style={{ fontSize: 13, opacity: 0.9, margin: 0 }}>
          Предварительный счёт до получения оплаты. Не является фискальным документом.
          После подтверждения оплаты система автоматически выставит фактуру VAT.
        </p>
      </div>

      {/* Add Button */}
      {!showForm && (
        <button
          className="btn btn-primary"
          style={{ width: '100%', marginBottom: 16 }}
          onClick={() => setShowForm(true)}
        >
          ➕ Создать Proforma
        </button>
      )}

      {/* Form */}
      {showForm && (
        <div style={{
          background: 'rgba(255,255,255,0.05)',
          borderRadius: 12,
          padding: 20,
          marginBottom: 16,
        }}>
          <h3 style={{ marginTop: 0 }}>Новая Proforma</h3>

          {/* Client */}
          <div style={{ marginBottom: 16 }}>
            <div style={{ fontWeight: 'bold', marginBottom: 8, opacity: 0.8 }}>👤 Клиент</div>
            <div style={{ display: 'flex', gap: 8, marginBottom: 8 }}>
              <button className="btn btn-small" style={{ flex: 1, opacity: buyerType==='company'?1:0.6 }} onClick={() => setBuyerType('company')}>🏢 Фирма</button>
              <button className="btn btn-small" style={{ flex: 1, opacity: buyerType==='person'?1:0.6 }} onClick={() => setBuyerType('person')}>👤 Физлицо</button>
            </div>
            <input
              type="text"
              value={form.client_name}
              onChange={(e) => setForm({ ...form, client_name: e.target.value })}
              placeholder="Название фирмы / Имя клиента *"
              style={{
                width: '100%', padding: 12, borderRadius: 8, marginBottom: 8,
                border: '1px solid rgba(255,255,255,0.2)',
                background: 'rgba(255,255,255,0.05)', color: '#fff',
              }}
            />
            {buyerType === 'company' && (
              <div style={{ display: 'flex', gap: 8 }}>
                <input
                  type="text"
                  value={form.client_nip}
                  onChange={(e) => setForm({ ...form, client_nip: e.target.value })}
                  placeholder="NIP / EU VAT"
                  style={{
                    flex: 1, padding: 12, borderRadius: 8,
                    border: '1px solid rgba(255,255,255,0.2)',
                    background: 'rgba(255,255,255,0.05)', color: '#fff',
                  }}
                />
                <button className="btn btn-small" onClick={() => lookupNip(form.client_nip)} disabled={nipLoading}>
                  {nipLoading ? '⏳' : '🔍 Szukaj'}
                </button>
              </div>
            )}
            {buyerType === 'person' && (
              <div style={{ fontSize: 12, opacity: 0.7, marginBottom: 4 }}>NIP не требуется для физлица</div>
            )}
            <input
              type="text"
              value={form.client_address}
              onChange={(e) => setForm({ ...form, client_address: e.target.value })}
              placeholder="Адрес"
              style={{
                width: '100%', padding: 12, borderRadius: 8, marginTop: 8,
                border: '1px solid rgba(255,255,255,0.2)',
                background: 'rgba(255,255,255,0.05)', color: '#fff',
              }}
            />
          </div>

          {/* Currency */}
          <div style={{ marginBottom: 16 }}>
            <div style={{ fontWeight: 'bold', marginBottom: 8, opacity: 0.8 }}>💱 Валюта</div>
            <select
              value={currency}
              onChange={(e) => setCurrency(e.target.value)}
              style={{ width: '100%', padding: 12, borderRadius: 8, border: '1px solid rgba(255,255,255,0.2)', background: 'rgba(255,255,255,0.05)', color: '#fff' }}
            >
              {bankAccounts.length === 0 && <option value="PLN">PLN</option>}
              {bankAccounts.map(acc => <option key={acc.id} value={acc.currency}>{acc.currency}</option>)}
            </select>
            {bankAccounts.length > 0 && (
              <div style={{ marginTop: 8, fontSize: 12, opacity: 0.8 }}>
                {(() => {
                  const acc = bankAccounts.find(a => a.currency === currency) || bankAccounts[0];
                  if (!acc) return null;
                  return (
                    <div>
                      {acc.bank_name && <div><b>Bank:</b> {acc.bank_name}</div>}
                      <div><b>IBAN:</b> {acc.iban}</div>
                      {acc.swift && <div><b>SWIFT:</b> {acc.swift}</div>}
                    </div>
                  );
                })()}
              </div>
            )}
          </div>

          {/* Service */}
          <div style={{ marginBottom: 16 }}>
            <div style={{ fontWeight: 'bold', marginBottom: 8, opacity: 0.8 }}>📦 Товар/Услуга</div>
            <input
              type="text"
              value={form.service_name}
              onChange={(e) => setForm({ ...form, service_name: e.target.value })}
              placeholder="Название услуги *"
              style={{
                width: '100%', padding: 12, borderRadius: 8, marginBottom: 8,
                border: '1px solid rgba(255,255,255,0.2)',
                background: 'rgba(255,255,255,0.05)', color: '#fff',
              }}
            />
            <div style={{ display: 'flex', gap: 8 }}>
              <input
                type="number"
                value={form.quantity}
                onChange={(e) => setForm({ ...form, quantity: parseFloat(e.target.value) || 1 })}
                placeholder="Кол-во"
                style={{
                  width: 80, padding: 12, borderRadius: 8,
                  border: '1px solid rgba(255,255,255,0.2)',
                  background: 'rgba(255,255,255,0.05)', color: '#fff',
                }}
              />
              <input
                type="number"
                value={form.unit_price}
                onChange={(e) => setForm({ ...form, unit_price: e.target.value })}
                placeholder="Цена netto *"
                style={{
                  flex: 1, padding: 12, borderRadius: 8,
                  border: '1px solid rgba(255,255,255,0.2)',
                  background: 'rgba(255,255,255,0.05)', color: '#fff',
                }}
              />
              <select
                value={form.vat_rate}
                onChange={(e) => setForm({ ...form, vat_rate: e.target.value })}
                style={{
                  width: 140, padding: 12, borderRadius: 8,
                  border: '1px solid rgba(255,255,255,0.2)',
                  background: 'rgba(255,255,255,0.05)', color: '#fff',
                }}
              >
                {VAT_OPTIONS.map(opt => (
                  <option key={opt.value} value={opt.value}>{opt.label}</option>
                ))}
              </select>
            </div>
          </div>

          {/* Payment terms */}
          <div style={{ marginBottom: 16 }}>
            <div style={{ fontWeight: 'bold', marginBottom: 8, opacity: 0.8 }}>💳 Срок оплаты</div>
            <select
              value={form.payment_days}
              onChange={(e) => setForm({ ...form, payment_days: parseInt(e.target.value) })}
              style={{
                width: '100%', padding: 12, borderRadius: 8,
                border: '1px solid rgba(255,255,255,0.2)',
                background: 'rgba(255,255,255,0.05)', color: '#fff',
              }}
            >
              <option value={7}>7 дней</option>
              <option value={14}>14 дней</option>
              <option value={30}>30 дней</option>
              <option value={60}>60 дней</option>
            </select>
          </div>

          {/* Payment method */}
          <div style={{ marginBottom: 16 }}>
            <div style={{ fontWeight: 'bold', marginBottom: 8, opacity: 0.8 }}>💰 Forma płatności</div>
            <select
              value={form.payment_method}
              onChange={(e) => setForm({ ...form, payment_method: e.target.value })}
              style={{
                width: '100%', padding: 12, borderRadius: 8,
                border: '1px solid rgba(255,255,255,0.2)',
                background: 'rgba(255,255,255,0.05)', color: '#fff',
              }}
            >
              <option value="przelew">💳 Przelew bankowy</option>
              <option value="gotowka">💵 Gotówka</option>
              <option value="karta">💳 Karta płatnicza</option>
            </select>
          </div>

          {/* Preview */}
          {form.unit_price && (
            <div style={{
              background: 'rgba(16,185,129,0.1)',
              borderRadius: 8,
              padding: 12,
              marginBottom: 16,
            }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 13 }}>
                <span>Netto:</span>
                <span>{(form.quantity * parseFloat(form.unit_price || 0)).toFixed(2)} PLN</span>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 13 }}>
                <span>VAT ({form.vat_rate}%):</span>
                <span>{(form.quantity * parseFloat(form.unit_price || 0) * form.vat_rate / 100).toFixed(2)} PLN</span>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', fontWeight: 'bold', marginTop: 4 }}>
                <span>Brutto:</span>
                <span>{(form.quantity * parseFloat(form.unit_price || 0) * (1 + form.vat_rate / 100)).toFixed(2)} PLN</span>
              </div>
            </div>
          )}

          <div style={{ display: 'flex', gap: 8 }}>
            <button className="btn btn-primary" style={{ flex: 1 }} onClick={createProforma}>
              📋 Создать Proforma
            </button>
            <button className="btn" style={{ background: 'rgba(255,255,255,0.1)' }} onClick={() => { setShowForm(false); setEditId(null); setEditCompanyId(null); }}>
              Отмена
            </button>
          </div>
        </div>
      )}

      {/* Proformas List */}
      {proformas.length === 0 ? (
        <div style={{
          textAlign: 'center',
          padding: 32,
          background: 'rgba(255,255,255,0.05)',
          borderRadius: 12,
        }}>
          <div style={{ fontSize: 48, marginBottom: 12 }}>📋</div>
          <p style={{ opacity: 0.7 }}>Нет фактур/проформ для выбранной компании</p>
        </div>
      ) : (
        <div className="list">
          {proformas.map(p => (
            <div key={p.id} style={{
              background: 'rgba(255,255,255,0.05)',
              borderRadius: 12,
              padding: 16,
              marginBottom: 8,
            }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 8 }}>
                <div>
                  <div style={{ fontWeight: 'bold' }}>{p.invoice_number}</div>
                  <div style={{ fontSize: 12, opacity: 0.7 }}>{p.buyer_name}</div>
                </div>
                <div style={{ textAlign: 'right' }}>
                  <div style={{ fontWeight: 'bold', color: '#10b981' }}>
                    {Number(p.gross_total || 0).toFixed(2)} {p.currency || 'PLN'}
                  </div>
                  <span style={{
                    background: statusColors[p.status],
                    padding: '2px 6px',
                    borderRadius: 4,
                    fontSize: 10,
                  }}>
                    {statusLabels[p.status] || p.status}
                  </span>
                </div>
              </div>
              <div style={{ fontSize: 11, opacity: 0.6, marginBottom: 8 }}>
                Срок: {p.due_date || '—'}
              </div>
              <div style={{ display: 'flex', gap: 8 }}>
                <button className="btn btn-small" style={{ background: 'rgba(59,130,246,0.3)' }} onClick={() => editProforma(p)} disabled={p.status !== 'proforma'}>✏️ Ред.</button>
                <button className="btn btn-small" style={{ background: 'rgba(239,68,68,0.3)' }} onClick={() => deleteProforma(p)} disabled={p.status !== 'proforma'}>🗑</button>
                <button
                  className="btn btn-pdf" style={{ color: "#111", fontWeight: 800 }}
                  onClick={() => sharePdf(p)}
                >
                  PDF
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
